import { Link } from '@tanstack/react-router';
import {
  type ColumnFiltersState,
  createColumnHelper,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { Download, Loader2, Settings } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { useGetAllClassesWithSearch } from '~entities/classes';
import { type ClassSearchDetails } from '~entities/classes/modal/class-search';
import { useGetStudioDetails } from '~entities/studios/api/get-studio-details';
import { useUpdatePaymentsSettings } from '~entities/studios/api/update-payments-settings';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import { Card, CardContent } from '~/shared/shadcn/ui/card';
import {
  EnrollioTable,
  EnrollioTableColumnToggle,
  EnrollioTableSearch,
  EnrollioTableSelectFilter,
} from '~shared/components/enrollio-table';
import {
  DataTableColumnHeader,
  DataTableColumnHeaderWithFilter,
} from '~shared/components/table';
import { DAYS, type Days } from '~shared/constants/days';
import {
  extractDateOnly,
  formatAgeRange,
  formatClassTime,
} from '~shared/helpers/datetime';
import { capitalizeWords } from '~shared/helpers/text-formatting';
import { useFilterPersistence } from '~shared/hooks/useFilterPersistence';
import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~shared/shadcn/ui/dropdown-menu';
import { Input } from '~shared/shadcn/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';

import { DownloadAttendanceSheetsDialog } from './download-attendance-sheets-dialog';

// Create a column helper
const columnHelper = createColumnHelper<ClassSearchDetails>();

function resolveClassTimes(v: ClassSearchDetails) {
  const firstAvailability = v.availability?.length
    ? v.availability[0]
    : undefined;
  const hasTopLevelTimes = Boolean(v.startTime && v.endTime);
  const hasAvailabilityTimes = Boolean(
    firstAvailability?.startTime && firstAvailability.endTime,
  );

  if (hasTopLevelTimes) {
    return { startTime: v.startTime, endTime: v.endTime };
  }
  if (hasAvailabilityTimes) {
    return {
      startTime: firstAvailability.startTime,
      endTime: firstAvailability.endTime,
    };
  }
  return {
    startTime: v.startTime ?? firstAvailability?.startTime,
    endTime: v.endTime ?? firstAvailability?.endTime,
  };
}

function timeToMinutes(time?: string | null) {
  const time24 = formatClassTime(time, 'HH:mm');
  if (!time24) return 0;
  const [hours = 0, minutes = 0] = time24.split(':').map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return 0;
  return hours * 60 + minutes;
}

// Define columns
const cols = [
  columnHelper.accessor('title', {
    header: (info) => (
      <DataTableColumnHeader
        className='pl-4 text-black'
        column={info.column}
        title='Class Name'
      />
    ),
    cell: (info) => (
      <Button
        variant={'link'}
        className='capitalize text-custom-secondary-1'
        asChild
      >
        <Link
          to={`/class-analytics/$classId`}
          params={{
            classId: info.row.original._id.toString(),
          }}
        >
          {info.getValue()}
        </Link>
      </Button>
    ),
  }),
  columnHelper.accessor('nextClassDate', {
    id: 'Next Class/Event',
    header: (info) => (
      <DataTableColumnHeader
        className='pl-4 text-black'
        column={info.column}
        title='Next Class/Event'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      if (!value) {
        return <div className='px-4 text-muted-foreground'>-</div>;
      }
      const date = new Date(value);
      const dayName = date.toLocaleDateString('en-US', {
        weekday: 'short',
        timeZone: 'UTC',
      });
      const formatted = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      });
      return <div className='w-36 px-4'>{`${dayName}, ${formatted}`}</div>;
    },
  }),
  columnHelper.accessor('location.name', {
    id: 'Location Name',
    header: (info) => (
      <DataTableColumnHeaderWithFilter
        className='pl-4 text-black'
        info={info}
        title='Location Name'
      />
    ),
    cell: (info) => (
      <div className='px-4 capitalize'>{info.getValue() ?? ''}</div>
    ),
  }),
  columnHelper.accessor('room.name', {
    id: 'Room',
    header: (info) => (
      <DataTableColumnHeaderWithFilter
        className='pl-4 text-black'
        info={info}
        title='Room'
      />
    ),
    cell: (info) => (
      <div className='px-4 capitalize'>{info.getValue() ?? ''}</div>
    ),
  }),
  columnHelper.accessor('maxSize', {
    id: 'Number of Seats',
    header: (info) => (
      <DataTableColumnHeader
        className='pl-4 text-black'
        column={info.column}
        title='Capacity'
      />
    ),
    cell: (info) => <div className='px-4'>{info.getValue()}</div>,
  }),
  columnHelper.accessor(
    (v) => {
      return formatAgeRange(v.startYear, v.startMonth, v.endYear, v.endMonth);
    },
    {
      id: 'Age (Range)',
      header: (info) => (
        <DataTableColumnHeaderWithFilter
          className='pl-4 text-black'
          info={info}
          title='Age (Range)'
        />
      ),
      cell: (info) => {
        return <div className='px-4'>{info.getValue() || ''}</div>;
      },
    },
  ),
  columnHelper.accessor('instructor', {
    id: 'Instructor',
    header: (info) => (
      <DataTableColumnHeader
        className='min-w-[200px] text-center text-black'
        column={info.column}
        title='Instructor'
      />
    ),
    cell: (info) => {
      const instructors = info.getValue();
      const visibleInstructors = instructors.slice(0, 3);
      const remainingCount = instructors.length - 3;
      const instructorNames = instructors
        .map((instructor) => capitalizeWords(instructor.name))
        .filter(Boolean)
        .join(', ');

      return (
        <>
          <div className='break-words text-sm font-medium md:hidden'>
            {instructorNames}
          </div>
          <div className='hidden flex-wrap gap-2 px-2 md:flex'>
            {visibleInstructors.map((v) => {
              const initials = v.name
                ? v.name
                    .split(' ')
                    .map((word) => word[0])
                    .join('')
                    .toUpperCase()
                : '';

              return (
                <TooltipProvider key={v._id}>
                  <Tooltip>
                    <TooltipTrigger>
                      <div className='flex size-8 items-center justify-center rounded-full bg-secondary text-sm font-medium text-secondary-foreground'>
                        {initials}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className='font-medium capitalize'>{v.name}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            })}
            {remainingCount > 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <div className='flex size-8 items-center justify-center rounded-full bg-muted text-sm font-medium text-muted-foreground'>
                      +{remainingCount}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className='flex flex-col gap-1'>
                      {instructors.slice(3).map((v) => (
                        <p key={v._id} className='font-medium capitalize'>
                          {v.name}
                        </p>
                      ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </>
      );
    },
  }),
  // columnHelper.accessor('session.fieldName', {
  //   header: (info) => (
  //     <DataTableColumnHeaderWithFilter
  //       className='pl-4 text-black'
  //       info={info}
  //       title='Session'
  //     />
  //   ),
  //   cell: (info) => {
  //     const session = info.getValue();
  //     return <div className='flex gap-2 px-4'>{session || ''}</div>;
  //   },
  // }),

  columnHelper.accessor(
    (v) => {
      const { startTime, endTime } = resolveClassTimes(v);

      const formattedStartTime = formatClassTime(startTime);
      const formattedEndTime = formatClassTime(endTime);

      if (!formattedStartTime && !formattedEndTime) {
        return '-';
      }
      if (!formattedStartTime) {
        return formattedEndTime ?? '-';
      }
      if (!formattedEndTime) {
        return formattedStartTime;
      }

      return `${formattedStartTime} - ${formattedEndTime}`;
    },
    {
      id: 'Time',
      header: (info) => (
        <DataTableColumnHeaderWithFilter
          className='pl-4 text-black'
          info={info}
          title='Time'
        />
      ),
      cell: (info) => <div className='w-40 px-4'>{info.getValue() || '-'}</div>,
      sortingFn: (rowA, rowB) =>
        timeToMinutes(resolveClassTimes(rowA.original).startTime) -
        timeToMinutes(resolveClassTimes(rowB.original).startTime),
    },
  ),
  columnHelper.accessor('startDate', {
    id: 'Start Date',
    header: (info) => (
      <DataTableColumnHeader
        className='pl-4 text-black'
        column={info.column}
        title='Start Date'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const extractedDate = value ? extractDateOnly(value) : null;
      if (!extractedDate) {
        return (
          <div className='flex gap-2 px-4'>
            <span>-</span>
          </div>
        );
      }

      // Use the date string directly with the format function
      // This avoids any Date object creation which can introduce timezone issues
      return (
        <div className='flex gap-2 px-4'>
          <span>
            {extractedDate.replace(/(\d{4})-(\d{2})-(\d{2})/, '$2/$3/$1')}
          </span>
        </div>
      );
    },
  }),
  columnHelper.accessor('endDate', {
    id: 'End Date',
    header: (info) => (
      <DataTableColumnHeader
        className='pl-4 text-black'
        column={info.column}
        title='End Date'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const extractedDate = value ? extractDateOnly(value) : null;
      if (!extractedDate) {
        return (
          <div className='flex gap-2 px-4'>
            <span>-</span>
          </div>
        );
      }

      // Use the date string directly with the format function
      // This avoids any Date object creation which can introduce timezone issues
      return (
        <div className='flex gap-2 px-4'>
          <span>
            {extractedDate.replace(/(\d{4})-(\d{2})-(\d{2})/, '$2/$3/$1')}
          </span>
        </div>
      );
    },
  }),
  columnHelper.accessor(
    (v) => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      return (v.days ?? []).join(', ');
    },
    {
      id: 'days',
      header: (info) => (
        <DataTableColumnHeaderWithFilter
          className='pl-4 text-black'
          info={info}
          title='Days'
        />
      ),
      cell: (info) => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        const days = info.row.original.days || [];
        return (
          <div className='flex gap-2 px-4'>
            {days.map((v) => {
              return <Badge key={v}>{v}</Badge>;
            })}
          </div>
        );
      },
    },
  ),

  columnHelper.accessor('tags', {
    id: 'Tags',
    header: (info) => (
      <DataTableColumnHeader
        className='pl-4 text-black'
        column={info.column}
        title='Tags'
      />
    ),
    cell: (info) => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const tags = info.getValue() || [];
      return (
        <div className='flex gap-2 px-4'>
          {tags.map((v) => {
            return (
              <Badge key={v._id} className='capitalize'>
                {v.name ?? ''}
              </Badge>
            );
          })}
        </div>
      );
    },
  }),
];

const initialVisibility = cols.reduce<VisibilityState>((acc, column) => {
  const id = column.id;
  if (id) {
    acc[id] = true;
  }
  return acc;
}, {});

const CLASS_SORT_KEY_MAP: Record<string, string> = {
  title: 'title',
  Session: 'sessionName',
  days: 'daysString',
  'Location Name': 'locationName',
  Room: 'roomName',
  'Age (Range)': 'ageRangeStart',
  Instructor: 'instructorNames',
  Time: 'startTime',
  'Start Date': 'startDate',
  'End Date': 'endDate',
  'Number of Seats': 'maxSize',
  'Tuition Fee': 'tuitionFee',
  Tags: 'tagsString',
};

export const AttendanceClassListTable = () => {
  // Use the filter persistence hook
  const { filters, pagination, updateFilter, updatePagination } =
    useFilterPersistence({
      query: '',
      day: 'All',
      tag: 'All',
      room: 'All',
      age: 'All',
      instructor: 'All',
      sessionId: 'All',
      showPastClasses: false,
      showArchivedClasses: false,
    });

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] =
    useState<VisibilityState>(initialVisibility);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const primarySort = sorting[0];
  const sortBy = primarySort
    ? (CLASS_SORT_KEY_MAP[primarySort.id] ?? primarySort.id)
    : undefined;
  const sortOrder = sortBy ? (primarySort?.desc ? 'desc' : 'asc') : undefined;

  const handleSortingChange = useCallback(
    (updater: React.SetStateAction<SortingState>) => {
      setSorting((prev) => {
        const nextSortedState =
          typeof updater === 'function' ? updater(prev) : updater;
        updatePagination((prevPagination) => ({
          ...prevPagination,
          pageIndex: 0,
        }));
        return nextSortedState;
      });
    },
    [updatePagination],
  );

  const { data: studioDetails } = useGetStudioDetails();
  const updateSettingsMutation = useUpdatePaymentsSettings();
  const { data: classesData, isFetching: isClassesFetching } =
    useGetAllClassesWithSearch({
      name: filters.query,
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      instructor: filters.instructor !== 'All' ? filters.instructor : undefined,
      tags: filters.tag !== 'All' ? filters.tag : undefined,
      days: filters.day !== 'All' ? (filters.day as Days) : undefined,
      room: filters.room !== 'All' ? filters.room : undefined,
      age: filters.age !== 'All' ? filters.age : undefined,
      sessionId: filters.sessionId !== 'All' ? filters.sessionId : undefined,
      studio: true,
      showPastClasses: filters.showPastClasses as boolean,
      // Same as All Classes: hide ended/archived classes unless the
      // Past Classes / Archived Classes toggles are on.
      isArchive: filters.showArchivedClasses,
      enforceStatusFilters: true,
      sortBy,
      sortOrder,
      sortByUpcoming: true,
    });

  const data = classesData?.data.classes ?? [];
  const totalCount = classesData?.pagination.totalCount ?? 0;
  const instructors = classesData?.data.instructors ?? [];
  const tags = classesData?.data.tags ?? [];
  const rooms = classesData?.data.rooms ?? [];
  const sessions = classesData?.data.sessions ?? [];
  const maxAge = classesData?.data.ages?.maxAge ?? 0;
  const minAge = classesData?.data.ages?.minAge ?? 0;
  const ages = Array.from(
    { length: maxAge - minAge + 1 },
    (_, i) => i + minAge,
  );

  const roomOptions = [
    { value: 'All', label: 'All Rooms' },
    ...rooms.map((room) => ({ value: room._id, label: room.name })),
  ];
  const tagOptions = [
    { value: 'All', label: 'All Tags' },
    ...tags.map((tag) => ({ value: tag.name, label: tag.name })),
  ];
  const sessionOptions = [
    { value: 'All', label: 'All Sessions' },
    ...sessions.map((session) => ({
      value: session._id,
      label: session.name,
    })),
  ];
  const ageOptions = [
    { value: 'All', label: 'All Ages' },
    ...ages.map((age) => ({ value: age.toString(), label: String(age) })),
  ];
  const instructorOptions = [
    { value: 'All', label: 'All Instructors' },
    ...instructors.map((instructor) => ({
      value: instructor.name,
      label: instructor.name,
    })),
  ];
  const dayOptions = [
    { value: 'All', label: 'All Days' },
    ...DAYS.map((day) => ({ value: day, label: day })),
  ];

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [triggerValue, setTriggerValue] = useState('');
  const [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false);

  const { hasAdminAccess } = useCurrentUserRole();

  useEffect(() => {
    if (studioDetails?.consecutiveAbsencesThreshold) {
      setTriggerValue(studioDetails.consecutiveAbsencesThreshold.toString());
    }
  }, [studioDetails]);

  const handleSave = async () => {
    const threshold = parseInt(triggerValue, 10);
    if (isNaN(threshold) || threshold < 1 || threshold > 30) {
      toast.error('Attendance trigger must be a number between 1 and 30.');
      return;
    }
    try {
      await updateSettingsMutation.mutateAsync({
        consecutiveAbsencesThreshold: parseInt(triggerValue),
      });
      toast.success('Attendance trigger settings updated successfully');
      handleCloseDialog();
    } catch (error) {
      toast.error('Failed to update attendance trigger settings');
      console.error('Failed to update attendance trigger settings', error);
    }
  };

  const handleCloseDialog = () => {
    if (studioDetails?.consecutiveAbsencesThreshold) {
      setTriggerValue(studioDetails.consecutiveAbsencesThreshold.toString());
    }
    setIsDialogOpen(false);
  };

  return (
    <Card>
      <CardContent className='p-2 pt-6 sm:p-6'>
        <DownloadAttendanceSheetsDialog
          open={isDownloadDialogOpen}
          onOpenChange={setIsDownloadDialogOpen}
        />
        <Dialog open={isDialogOpen} onOpenChange={handleCloseDialog}>
          <DialogContent className='sm:max-w-[425px]'>
            <DialogHeader>
              <DialogTitle>Attendance Trigger</DialogTitle>
              <DialogDescription>
                Set the number of days a student must be absent (not
                consecutively) before this workflow is triggered. Applies across
                all classes.
              </DialogDescription>
            </DialogHeader>
            <div className='grid gap-4 py-4'>
              <div className='grid gap-2'>
                <Input
                  id='attendance-trigger'
                  type='number'
                  placeholder='Enter trigger value'
                  className='w-full'
                  value={triggerValue}
                  onChange={(e) => {
                    let value = e.target.value;
                    if (value === '' || /^\d+$/.test(value)) {
                      // remove leading zeros
                      if (value.length > 1 && value.startsWith('0')) {
                        value = value.replace(/^0+/, '');
                      }
                      setTriggerValue(value);
                    }
                  }}
                />
                {Number(triggerValue) < 1 || Number(triggerValue) > 30 ? (
                  <span className='text-sm text-red-600'>
                    Enter a number between 1 and 30.
                  </span>
                ) : null}
              </div>
            </div>
            <div className='flex justify-end'>
              <Button
                onClick={handleSave}
                disabled={
                  Number(triggerValue) < 1 ||
                  Number(triggerValue) > 30 ||
                  updateSettingsMutation.isPending
                }
              >
                {updateSettingsMutation.isPending && (
                  <Loader2 className='mr-2 size-4 animate-spin' />
                )}
                Save
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <EnrollioTable
          ariaLabel='Classes available for attendance'
          totalLabel='Classes'
          filterHeader={
            <>
              <EnrollioTableSearch
                placeholder='Search classes...'
                value={filters.query}
                onChange={(value) => {
                  updateFilter('query', value);
                }}
              />
              <EnrollioTableSelectFilter
                className='w-[160px] capitalize'
                placeholder='All Rooms'
                value={filters.room}
                onChange={(value) => {
                  updateFilter('room', value);
                }}
                options={roomOptions}
              />
              <EnrollioTableSelectFilter
                className='w-[160px] capitalize'
                placeholder='All Tags'
                value={filters.tag}
                onChange={(value) => {
                  updateFilter('tag', value);
                }}
                options={tagOptions}
              />
              <EnrollioTableSelectFilter
                className='w-[160px] capitalize'
                placeholder='All Sessions'
                value={filters.sessionId}
                onChange={(value) => {
                  updateFilter('sessionId', value);
                }}
                options={sessionOptions}
              />
              <EnrollioTableSelectFilter
                className='w-[160px]'
                placeholder='All Ages'
                value={filters.age}
                onChange={(value) => {
                  updateFilter('age', value);
                }}
                options={ageOptions}
              />
              <EnrollioTableSelectFilter
                className='w-[160px] capitalize'
                placeholder='All Instructors'
                value={filters.instructor}
                onChange={(value) => {
                  updateFilter('instructor', value);
                }}
                options={instructorOptions}
              />
              <EnrollioTableSelectFilter
                className='w-[160px]'
                placeholder='All Days'
                value={filters.day}
                onChange={(value) => {
                  updateFilter('day', value);
                }}
                options={dayOptions}
              />
              {/* Show Past Classes Checkbox */}
              <div className='flex items-center space-x-2'>
                <Checkbox
                  id='showPastClasses'
                  checked={filters.showPastClasses as boolean}
                  onCheckedChange={(checked) => {
                    updateFilter('showPastClasses', checked === true);
                  }}
                />
                <label
                  htmlFor='showPastClasses'
                  className='cursor-pointer whitespace-nowrap text-sm font-medium'
                >
                  Past Classes
                </label>
              </div>
              {/* Show Archived Classes Checkbox */}
              <div className='flex items-center space-x-2'>
                <Checkbox
                  id='showArchivedClasses'
                  checked={filters.showArchivedClasses}
                  onCheckedChange={(checked) => {
                    updateFilter('showArchivedClasses', checked === true);
                  }}
                />
                <label
                  htmlFor='showArchivedClasses'
                  className='cursor-pointer whitespace-nowrap text-sm font-medium'
                >
                  Archived Classes
                </label>
              </div>
            </>
          }
          toolbar={(table) => (
            <>
              <Button
                variant='outline'
                size='icon'
                className='size-8 shrink-0'
                title='Download Attendance Sheets'
                aria-label='Download Attendance Sheets'
                onClick={() => {
                  setIsDownloadDialogOpen(true);
                }}
              >
                <Download className='size-4' />
              </Button>
              {hasAdminAccess && (
                <DropdownMenu>
                  <DropdownMenuTrigger disabled={!hasAdminAccess} asChild>
                    <Button
                      variant='ghost'
                      size='icon'
                      className='[&_svg]:size-6'
                    >
                      <Settings />
                      <span className='sr-only'>Settings</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align='end'>
                    <DropdownMenuItem
                      onClick={() => {
                        setIsDialogOpen(true);
                      }}
                    >
                      Attendance Trigger
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <EnrollioTableColumnToggle table={table} />
            </>
          )}
          columns={cols}
          data={data}
          rowCount={totalCount}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          columnFilters={columnFilters}
          onColumnFiltersChange={setColumnFilters}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          pagination={pagination}
          onPaginationChange={updatePagination}
          isUpdating={isClassesFetching}
          manualPagination
          manualSorting
        />
      </CardContent>
    </Card>
  );
};
